import { isEmpty, uniq } from 'lodash';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  IWidgetCascadeCombination,
  IWidgetCascadeConfig,
  IWidgetCascadeField,
  IWidgetCascadeRequestField,
} from '../../IMultiPreferenceWidget';
import { computeMultiPreferenceTransformId } from '../../computeMultiPreferenceTransforms';
import {
  IMultiPreferenceRow,
  multiPreferenceFieldKey,
} from '../../createMultiPreferenceFormSchema';

/**
 *  Controllo se le colonne della combinazione sono uguali alle colonne della richiesta:
 *  se sono uguali non li faccio vedere
 */
function checkColumnsValueEqual(
  configFields: IWidgetCascadeField[],
  value: IMultiPreferenceRow[],
  i: number,
  comb: IWidgetCascadeCombination,
) {
  let isEqual = true;
  for (const field of configFields) {
    if (!value[i]?.[multiPreferenceFieldKey(field.index)]) return false;
    isEqual = isEqual && comb.values[field.index] === value[i][multiPreferenceFieldKey(field.index)]; // prettier-ignore
  }
  return isEqual;
}

/**
 * Gestione della mutua esclusione delle colonne
 */
export function mutualExclusion(
  combinations: IWidgetCascadeCombination[],
  value: IMultiPreferenceRow[],
  configFields: IWidgetCascadeField[],
  index: number,
) {
  // Se la richiesta non ha valori, ritorno le combinazioni
  if (!value?.[0]) return combinations;

  // Se la richiesta ha valori, controllo se la combinazione è uguale a quella già presente nella richiesta
  const availableCombinations = combinations.filter(comb => {
    for (let i = 0; i < value.length; i++) {
      // non filtro la combinazione corrente
      if (i === index) continue;

      // Se la combinazione è uguale a quella già presente nella richiesta, non la mostro
      if (checkColumnsValueEqual(configFields, value, i, comb)) return false; // prettier-ignore
    }
    return true;
  });
  return availableCombinations;
}

/**
 * Logica per gestire la selezione a preferenza multipla. Si occupa di:
 *  - Calcolare la visibilità dei campi da mostrare e il loro stato (abilitato)
 *  - Reimpostare i campi in seguito alla modifica di una voce precedente
 *  - Gestire le opzioni disponibili alla voce successiva in base alle opzioni precedenti (mutua esclusione)
 *
 * @param config Configurazione del widget per il cascader
 * @param index Indice della riga corrente
 * @param name Nome del widget nel form schema
 */
export function useMultiPreferenceSelect(
  config: IWidgetCascadeConfig | undefined,
  index: number,
  name: string,
) {
  const configFields = config?.fields ?? [];

  const value: IMultiPreferenceRow[] = useWatch({
    name: name,
    defaultValue: [],
  });

  // I valori selezionati nel form servono per pre-filtrare le opzioni dei
  // passi successivi
  const selectedRow: IMultiPreferenceRow = useWatch({
    name: `${name}[${index}]`,
  });
  const selectedValues: Array<string | null> = configFields.map(field => {
    return selectedRow?.[multiPreferenceFieldKey(field.index)];
  });

  const { setValue } = useFormContext();

  /**
   * Quando cambio un valore, resetto i campi successivi
   * per garantire che non ci siano combinazioni non valide
   * (es. se ho selezionato "A" e poi cambio a "B", resetto i campi successivi)
   * @param value Valore selezionato
   * @param i Indice del campo corrente
   */
  const handleResetOnChange = (value: string, i: number) => {
    if (value) {
      for (let j = i; j < configFields.length - 1; j++) {
        setValue(`${name}[${index}].${multiPreferenceFieldKey(j + 1)}`, null, {
          shouldValidate: false,
          shouldDirty: false,
          shouldTouch: false,
        });
      }
    }
  };

  if (!config) return { fields: [], handleResetOnChange };

  // Se non ho selezionato nessun valore, ritorno tutte le combinazioni altrimenti filtro
  const fields = configFields.reduce((fields, field, i, arr) => {
    const availableCombinations =
      i === 0
        ? mutualExclusion(config.combinations, value, configFields, index)
        : fields[i - 1].availableCombinations.filter(c => {
            return fields.every(prevField => {
              const prevFieldSelectedValue =
                selectedValues[prevField.field.index];
              return (
                !prevFieldSelectedValue ||
                prevFieldSelectedValue === c.values[prevField.field.index]
              );
            });
          });

    fields.push({
      field,
      value: selectedValues[i],
      // Disabilitiamo sempre i campi successivi a quello da configurare per
      // garantire che si possa arrivare a una combinazione valida
      disabled: i === 0 ? false : isEmpty(fields[i - 1].value),
      // Le portiamo appresso solo per essere uste con `fields[i - 1]`
      availableCombinations,
      options: uniq(
        availableCombinations.map(comb => comb.values[field.index]),
      ).map((value, j) => ({
        label: value,
        value,
      })),
    });

    return fields;
  }, [] as IWidgetCascadeRequestField[]);

  return {
    fields,
    handleResetOnChange,
    /**
     * Calcolo dell'ID della riga corrente. Serve per impostare il valore
     * in fase di Rendering (React), viene poi consolidato in fase di
     * `transform`.
     */
    computedIdValue: computeMultiPreferenceTransformId(selectedValues),
  };
}
